// EditSection.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import FullScreenOverlay from "../../common/FullScreenOverlay";
import AddItemOverlay from "./AddItemOverlay";
import Spacer from "../../common/Spacer";
import IconButton from "../../common/Button";
import CustomRadioButton from "../../common/CustomRadioButton";
import { DragIcon, DeleteIcon, PlusIcon, EditIcon } from "../../common/Icons";
import DropIndicator from "../../common/DropIndicator";
import TextField from "../../common/TextField";
import // fetchDefaultContacts,
// createDefaultContact,
// updateDefaultContactService,
// deleteDefaultContactService,
"../../../services/dashboardServices";
import {
  fetchDefaultContacts,
  createDefaultContact,
  updateDefaultContact,
  deleteDefaultContact,
} from "../../../redux/actions/careplanActions";
import Menu from "../../common/Menu";

const EditSection = ({ section, allItems, onClose, onSave, modalButtons }) => {
  const dispatch = useDispatch();

  const defaultContacts = useSelector((s) => s.careplan.carePlan.contacts);
  const defaultContactsLoaded = useSelector(
    (s) => s.careplan.defaultContactsLoaded
  );
  const [itemData, setItemData] = useState(section.items);
  const [serviceUserSpecific, setServiceUserSpecific] = useState(
    section.isServiceUserSpecific
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // const [draggingCard, setDraggingCard] = useState(null);
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(
    section.items.map((item) => item.id)
  );
  // Default contacts related states
  // const [defaultContacts, setDefaultContacts] = useState([]);
  const [newDefault, setNewDefault] = useState({ name: "", number: "" });
  // For editing an existing contact (popup flow):
  const [editContact, setEditContact] = useState(null); // store the entire contact object being edited
  const [editForm, setEditForm] = useState({ name: "", number: "" });
  const [showEditOverlay, setShowEditOverlay] = useState(false);
  const [showEditSaveConfirm, setShowEditSaveConfirm] = useState(false); // "Are you sure you want to save?"

  // For deleting an existing contact:
  const [deleteContact, setDeleteContact] = useState(null); // store contact object
  const [showDeleteContactOverlay, setShowDeleteContactOverlay] =
    useState(false);

  const [showAddItemOverlay, setShowAddItemOverlay] = useState(false);

  const scrollSpeed = 20; // Adjust the scroll speed as needed
  const scrollThreshold = 100; // The distance from the edge to start scrolling
  const autoScrollInterval = useRef(null); // Use useRef to persist across renders
  const clientYRef = useRef(0);

  // Function to start auto-scrolling
  const startAutoScroll = useCallback(() => {
    if (autoScrollInterval.current) return;

    autoScrollInterval.current = setInterval(() => {
      const { innerHeight } = window;
      const clientY = clientYRef.current;

      if (clientY < scrollThreshold) {
        window.scrollBy(0, -scrollSpeed);
      } else if (clientY > innerHeight - scrollThreshold) {
        window.scrollBy(0, scrollSpeed);
      }
    }, 16);
  }, [scrollThreshold, scrollSpeed]);

  // Function to stop auto-scrolling
  const stopAutoScroll = useCallback(() => {
    if (autoScrollInterval.current) {
      clearInterval(autoScrollInterval.current);
      autoScrollInterval.current = null;
    }
  }, []);

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();

      clientYRef.current = e.clientY;

      const { innerHeight } = window;

      if (
        e.clientY < scrollThreshold ||
        e.clientY > innerHeight - scrollThreshold
      ) {
        startAutoScroll();
      } else {
        stopAutoScroll();
      }
    },
    [scrollThreshold, startAutoScroll, stopAutoScroll]
  );

  const handleDropOutside = useCallback(
    (e) => {
      e.preventDefault();
      stopAutoScroll();
    },
    [stopAutoScroll]
  );

  const handleDragEnd = useCallback(() => {
    setDraggingItemId(null);

    document.removeEventListener("dragover", handleDragOver, {
      passive: false,
    });
    document.removeEventListener("dragend", handleDragEnd);
    document.removeEventListener("drop", handleDropOutside);

    stopAutoScroll();
  }, [handleDragOver, handleDropOutside, stopAutoScroll]);

  const handleDropIndicatorDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.style.opacity = "1";
  };

  const handleDropIndicatorDragLeave = (e) => {
    e.currentTarget.style.opacity = "0";
  };

  const handleDragStart = useCallback(
    (itemId) => {
      setDraggingItemId(itemId);

      // Add event listeners
      document.addEventListener("dragover", handleDragOver, { passive: false });
      document.addEventListener("dragend", handleDragEnd);
      document.addEventListener("drop", handleDropOutside);
    },
    [handleDragOver, handleDragEnd, handleDropOutside]
  );

  useEffect(() => {
    return () => {
      stopAutoScroll();
      document.removeEventListener("dragover", handleDragOver, {
        passive: false,
      });
      document.removeEventListener("dragend", handleDragEnd);
      document.removeEventListener("drop", handleDropOutside);
    };
  }, [stopAutoScroll, handleDragOver, handleDragEnd, handleDropOutside]);

  // Only fetch default contacts if this is the 'Default Contacts' section
  useEffect(() => {
    if (section.id === 6 && !defaultContactsLoaded) {
      dispatch(fetchDefaultContacts());
    }
  }, [section.id, defaultContactsLoaded, dispatch]);

  useEffect(() => {
    if (!serviceUserSpecific) {
      setItemData(
        section.items.map((item, idx) => ({ ...item, order: idx + 1 }))
      );
      setSelectedItemIds(section.items.map((item) => item.id));
    }
  }, [serviceUserSpecific, section.items]);

  const handleDropIndicatorDrop = (e, beforeItemId) => {
    e.preventDefault();
    e.currentTarget.style.opacity = "0";

    stopAutoScroll();

    if (draggingItemId === null) return;

    const fromIndex = itemData.findIndex((item) => item.id === draggingItemId);
    if (fromIndex === -1) return;

    let updatedItems = [...itemData];

    if (beforeItemId === draggingItemId) {
      return;
    }

    let dropIndex;
    if (beforeItemId === null) {
      // Drop at the end
      dropIndex = updatedItems.length;
    } else {
      dropIndex = updatedItems.findIndex((item) => item.id === beforeItemId);
    }

    if (dropIndex > fromIndex) {
      dropIndex--;
    }

    const [draggedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(dropIndex, 0, draggedItem);

    // Reassign orders
    updatedItems = updatedItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setItemData(updatedItems);
    setDraggingItemId(null);
  };

  const handleSaveConfirm = () => {
    // Prepare the items data
    const itemsData = itemData.map((item) => ({
      id: item.id,
      order: item.order,
    }));

    // Prepare the data to send to the parent
    const updatedSection = {
      sectionId: section.id,
      isServiceUserSpecific: serviceUserSpecific,
      items: itemsData,
    };

    // Call the onSave prop with the updated section data
    onSave(updatedSection);

    // Close the confirmation modal
    setShowConfirmModal(false);
  };

  const handleSaveClick = () => {
    setShowConfirmModal(true);
  };

  const handleDeleteClick = (itemId) => {
    setItemToDelete(itemId); // Store the item to be deleted
    setShowDeleteConfirmModal(true); // Show confirmation modal
  };

  const handleRemoveItem = () => {
    // Update selectedItemIds state to remove the given item
    setSelectedItemIds((prevSelectedIds) =>
      prevSelectedIds.filter((id) => id !== itemToDelete)
    );

    // Update itemData state to remove the given item
    setItemData((prevItems) =>
      prevItems.filter((item) => item.id !== itemToDelete)
    );
    setItemToDelete(null);
    setShowDeleteConfirmModal(false);
  };

  const handleRadioChange = (value) => {
    setServiceUserSpecific(value);
  };

  const handleUpdateSelectedItems = (newSelectedIds) => {
    // Update the selected IDs state
    setSelectedItemIds(newSelectedIds);

    // Keep items already in itemData that are still selected
    const keptItems = itemData.filter((item) =>
      newSelectedIds.includes(item.id)
    );

    // Find new items that are selected but not currently in itemData
    const newItems = allItems.filter(
      (item) =>
        newSelectedIds.includes(item.id) &&
        !itemData.some((existing) => existing.id === item.id)
    );

    // Combine the kept items with new items (appending new items at the bottom)
    let updatedItems = [...keptItems];

    newItems.forEach((item) => {
      updatedItems.push({
        ...item,
        // Set the order of the new item to be at the bottom
        order: updatedItems.length + 1,
      });
    });

    // Reassign orders sequentially based on current array index to reflect visual order
    updatedItems = updatedItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    // Update the state with the new list and close the overlay
    setItemData(updatedItems);
    setShowAddItemOverlay(false);
  };

  const formatUKPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "");
    let formattedNumber = "";

    if (cleaned.length <= 5) {
      // If the input is up to 5 digits, treat it as part of the area code
      formattedNumber = cleaned;
    } else if (cleaned.length <= 11) {
      // Format as '01274 497121' when the total number is between 6 and 11 digits
      formattedNumber = `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    } else {
      // For longer inputs (though in the UK format, 11 digits are typical), break it into parts
      formattedNumber = `${cleaned.slice(0, 5)} ${cleaned.slice(
        5,
        9
      )} ${cleaned.slice(9, 13)}`;
    }

    return formattedNumber;
  };

  // A simple UK phone regex (adjust as needed, e.g. if you allow 07 mobile formats or more).
  function isValidUKPhoneNumber(phone) {
    // This is just an example pattern for 0XXX XXXXXX or 0XXXX XXXXXX, etc.
    // Adjust to match your exact requirements.
    const ukRegex = /^0\d{3,4}\s?\d{5,7}$/;
    return ukRegex.test(phone.trim());
  }

  // Called when user types into "Add new default contact" fields
  const handleNewDefaultChange = (e) => {
    const { name, value } = e.target;
    // Map the input field names to the state keys

    // Clear custom validation when the user types
    if (name === "defaultContactAddNumber") {
      const addNumberField = document.querySelector(
        'input[name="defaultContactAddNumber"]'
      );
      if (addNumberField) {
        addNumberField.setCustomValidity(""); // Clear custom validation
      }
    }

    if (name === "defaultContactAddName") {
      const addNameField = document.querySelector(
        'input[name="defaultContactAddName"]'
      );
      if (addNameField) {
        addNameField.setCustomValidity(""); // Clear custom validation
      }
    }

    const stateKey = name === "defaultContactAddName" ? "name" : "number";

    // If it's the number field, remove everything except digits
    if (name === "defaultContactAddNumber") {
      const digitsOnly = value.replace(/\D/g, ""); // remove non‐digits
      // If you still want phone formatting, pass `digitsOnly` to your format function
      const formatted = formatUKPhoneNumber(digitsOnly);
      setNewDefault((prev) => ({ ...prev, [stateKey]: formatted }));
    } else {
      // For the name field, just store it as is
      setNewDefault((prev) => ({ ...prev, [stateKey]: value }));
    }
  };

  // Called when user submits "Add new default contact"
  const handleAddDefault = async (e) => {
    e.preventDefault();

    const addNameField = document.querySelector(
      'input[name="defaultContactAddName"]'
    );
    const addNumberField = document.querySelector(
      'input[name="defaultContactAddNumber"]'
    );

    // Check if fields are empty
    if (!newDefault.name.trim()) {
      addNameField.setCustomValidity("Please enter a name.");
      addNameField.reportValidity();
      return;
    } else {
      addNameField.setCustomValidity("");
    }

    if (!newDefault.number.trim()) {
      addNumberField.setCustomValidity("Please enter a phone number.");
      addNumberField.reportValidity();
      return;
    } else if (!isValidUKPhoneNumber(newDefault.number)) {
      addNumberField.setCustomValidity("Please enter a valid UK phone number.");
      addNumberField.reportValidity();
      return;
    } else {
      addNumberField.setCustomValidity("");
    }

    if (!newDefault.name || !newDefault.number) {
      alert("Please fill in both name and number.");
      return;
    }
    try {
      await dispatch(createDefaultContact(newDefault));
      setNewDefault({ name: "", number: "" });
    } catch (err) {
      console.error("Failed to add default contact", err);
    }
  };

  // Start editing an existing default contact
  // const handleStartEdit = (contact) => {
  //   setSelectedContactId(contact.id);
  //   setEditForm({ name: contact.name, number: contact.number });
  // };

  // Update local state as user types in the edit form
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    // Map the input field names to the state keys

    // Clear custom validation when the user types
    if (name === "defaultContactEditNumber") {
      const editNumberField = document.querySelector(
        'input[name="defaultContactEditNumber"]'
      );
      if (editNumberField) {
        editNumberField.setCustomValidity(""); // Clear custom validation
      }
    }

    if (name === "defaultContactEditName") {
      const editNameField = document.querySelector(
        'input[name="defaultContactEditName"]'
      );
      if (editNameField) {
        editNameField.setCustomValidity(""); // Clear custom validation
      }
    }
    const stateKey = name === "defaultContactEditName" ? "name" : "number";

    // If it's the number field, remove everything except digits
    if (name === "defaultContactEditNumber") {
      const digitsOnly = value.replace(/\D/g, ""); // remove non‐digits
      // If you still want phone formatting, pass `digitsOnly` to your format function
      const formatted = formatUKPhoneNumber(digitsOnly);
      setEditForm((prev) => ({ ...prev, [stateKey]: formatted }));
    } else {
      // For the name field, just store it as is
      setEditForm((prev) => ({ ...prev, [stateKey]: value }));
    }
  };

  // If the user says "Yes, save" in the confirm overlay
  const handleConfirmSaveContact = async () => {
    try {
      // Actually call update API
      await dispatch(
        updateDefaultContact({ id: editContact.id, data: editForm })
      );
      // Replace old contact in array
      // setDefaultContacts((prev) =>
      //   prev.map((c) => (c.id === editContact.id ? updated : c))
      // );
      // Close overlays
      setShowEditSaveConfirm(false);
      setShowEditOverlay(false);
      setEditContact(null);
    } catch (err) {
      console.error("Failed to update default contact", err);
    }
  };

  const handleEditOverlaySaveClick = () => {
    const editNameField = document.querySelector(
      'input[name="defaultContactEditName"]'
    );
    const editNumberField = document.querySelector(
      'input[name="defaultContactEditNumber"]'
    );

    // Check if fields are empty
    if (!editForm.name.trim()) {
      editNameField.setCustomValidity("Please enter a name.");
      editNameField.reportValidity();
      return;
    } else {
      editNameField.setCustomValidity("");
    }

    if (!editForm.number.trim()) {
      editNumberField.setCustomValidity("Please enter a phone number.");
      editNumberField.reportValidity();
      return;
    } else if (!isValidUKPhoneNumber(editForm.number)) {
      editNumberField.setCustomValidity(
        "Please enter a valid UK phone number."
      );
      editNumberField.reportValidity();
      return;
    } else {
      editNumberField.setCustomValidity("");
    }

    setShowEditSaveConfirm(true);
  };

  // --------------------------------------------
  //   Delete Contact Confirmation Overlay
  // --------------------------------------------
  async function handleConfirmDeleteContact() {
    if (!deleteContact) return;
    try {
      // await deleteDefaultContactService(deleteContact.id);
      // setDefaultContacts((prev) =>
      //   prev.filter((c) => c.id !== deleteContact.id)
      // );
      await dispatch(deleteDefaultContact(deleteContact.id));
      setShowDeleteContactOverlay(false);
      setDeleteContact(null);
    } catch (err) {
      console.error("Failed to delete default contact", err);
    }
  }

  const handleMenuClick = (contact, action) => {
    if (action === "edit") {
      setEditContact(contact);
      setEditForm({ name: contact.name, number: contact.number });
      setShowEditOverlay(true);
    } else if (action === "delete") {
      setDeleteContact(contact);
      setShowDeleteContactOverlay(true);
    }
  };

  // Popup Menu Items
  const getMenuItems = (row) => [
    {
      label: "Edit Contact",
      icon: <EditIcon />,
      action: () => handleMenuClick(row, "edit"),
      // action: () => {
      //   setViewCarePlanId(row.id); // Assuming 'id' is the carePlanId
      // },
    },
    {
      label: "Delete Contact",
      icon: <DeleteIcon />,
      action: () => handleMenuClick(row, "delete"),
      // action: () => setEditCarePlanNHS(row.nhsNumber),
    },
  ];

  // Buttons for edit overlay
  const editOverlayButtons = [
    {
      label: "Cancel",
      onClick: () => {
        setShowEditOverlay(false);
        setEditContact(null);
      },
    },
    {
      label: "Update",
      onClick: () => handleEditOverlaySaveClick(),
      variant: "primary",
    },
  ];

  return (
    <>
      <FullScreenOverlay
        title="Edit Section"
        onClose={onClose}
        width="100%"
        height="100%"
        buttons={
          section.id === 6
            ? [{ label: "Close", onClick: onClose }]
            : [
                ...modalButtons,
                { label: "Save", onClick: handleSaveClick, variant: "primary" },
              ]
        }
      >
        <FormSection>
          <p className="body-text-large medium neutral-600">Section title</p>
          <Spacer height="8px" />
          <FormTitle className="semi-bold">{section.title}</FormTitle>
        </FormSection>

        {/* hide items in section for Urgent care section */}
        {section.id !== 6 && (
          <FormSection>
            <FormTitle className="semi-bold">Items in section</FormTitle>
            <Spacer height="8px" />
            <p className="body-text-regular regular neutral-800">
              Select how the items in this section can be selected when creating
              a Care Plan
            </p>
            <RadioGroup>
              <RadioButtonWrapper>
                <CustomRadioButton
                  label="Set items with add other items"
                  name="itemOption"
                  checked={!serviceUserSpecific}
                  onChange={() => handleRadioChange(false)}
                />
              </RadioButtonWrapper>

              <RadioButtonWrapper>
                <CustomRadioButton
                  label="Service user specific items with add other items"
                  name="itemOption"
                  checked={serviceUserSpecific}
                  onChange={() => handleRadioChange(true)}
                />
              </RadioButtonWrapper>
            </RadioGroup>
          </FormSection>
        )}

        {section.id === 6 && (
          <FormSection>
            <ItemTitle className="semi-bold">Default Contacts</ItemTitle>
            <Spacer height="8px" />

            <div className="table-wrapper">
              <table className="care-plan-table">
                <thead>
                  <tr>
                    <th>
                      <div className="header-content">
                        Name
                        <span className="header-icon"></span>
                      </div>
                    </th>
                    <th>
                      <div className="header-content">
                        Phone Number
                        <span className="header-icon"></span>
                      </div>
                    </th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {/* Existing default contacts */}
                  {defaultContacts.map((contact) => {
                    return (
                      <tr key={contact.id}>
                        {/* Name column */}
                        <td>{contact.name}</td>

                        {/* Phone Number column */}
                        <td>{contact.number}</td>

                        {/* Edit column */}
                        <td>
                          <span className="draftplan-menu-icon">
                            <Menu menuItems={getMenuItems(contact)} />
                          </span>
                        </td>
                      </tr>
                    );
                  })}

                  {/* Row to add new default contact */}
                </tbody>
              </table>
            </div>
            <Spacer height="16px" />
            <ItemTitle className="semi-bold">Add new Default Contact</ItemTitle>
            <Spacer height="8px" />
            <form
              id="contacAddForm"
              onSubmit={(e) => e.preventDefault()}
              style={{
                width: "100%", // Fill the entire container width
                display: "grid", // Grid layout
                gridTemplateColumns: "1fr 1fr auto",
                // ^ Two equal columns for the text fields,
                //   and an auto-width column for the button
                gap: "16px", // Spacing between columns
                alignItems: "center", // Vertically center them
              }}
            >
              <TextField
                type="text"
                name="defaultContactAddName"
                placeholder="New contact name"
                value={newDefault.name}
                onChange={handleNewDefaultChange}
                style={{ width: "100%" }}
                required
              />

              <TextField
                type="text"
                name="defaultContactAddNumber"
                placeholder="New contact number"
                value={newDefault.number}
                onChange={handleNewDefaultChange}
                style={{ width: "100%" }}
                required
              />

              <IconButton onClick={handleAddDefault}>Add</IconButton>
            </form>
          </FormSection>
        )}

        {!serviceUserSpecific && section.id !== 6 && (
          <FormSection>
            <ItemTitle className="semi-bold">Items</ItemTitle>
            <Spacer height="8px" />
            <p className="body-text-regular regular neutral-800">
              Add items and arrange the order to set this section for all Care
              Plans
            </p>
            <Spacer height="24px" />

            {itemData.map((item) => (
              <React.Fragment key={item.id}>
                <DropIndicator
                  beforeItemId={item.id}
                  onDragOver={handleDropIndicatorDragOver}
                  onDragLeave={handleDropIndicatorDragLeave}
                  onDrop={(e) => handleDropIndicatorDrop(e, item.id)}
                />
                <DragWrapper
                  draggable
                  onDragStart={() => handleDragStart(item.id)}
                  onDragEnd={handleDragEnd}
                >
                  <SectionCard mode="edit">
                    <ItemTitleWrapper>
                      <ItemTitleButtonWrapper>
                        <DragIcon />
                        <ItemTitle className="semi-bold">
                          {item.title}
                        </ItemTitle>
                      </ItemTitleButtonWrapper>
                      <IconButtonWrapper
                        onClick={() => handleDeleteClick(item.id)}
                      >
                        <IconButton
                          variant="ghost"
                          isRemove={true}
                          noPadding={true}
                          icon={<DeleteIcon color="#EC2D30" />}
                        >
                          Remove
                        </IconButton>
                      </IconButtonWrapper>
                    </ItemTitleWrapper>
                    <EditItemCard>
                      <ItemThumbnail>
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.title} />
                        ) : (
                          <div></div>
                        )}
                      </ItemThumbnail>
                      <ItemDescription
                        mode="edit"
                        className="body-text-regular regular neutral-600"
                      >
                        {item.description}
                      </ItemDescription>
                    </EditItemCard>
                  </SectionCard>
                </DragWrapper>
              </React.Fragment>
            ))}
            <DropIndicator
              beforeItemId={null}
              onDragOver={handleDropIndicatorDragOver}
              onDragLeave={handleDropIndicatorDragLeave}
              onDrop={(e) => handleDropIndicatorDrop(e, null)}
            />
            <AddItemButton>
              <IconButton
                variant="ghost"
                state="idle"
                icon={<PlusIcon color="#004F96" />}
                onClick={() => setShowAddItemOverlay(true)}
              >
                Add new item
              </IconButton>
            </AddItemButton>
          </FormSection>
        )}
      </FullScreenOverlay>

      {/* Confirmation Modals */}
      {showConfirmModal && (
        <FullScreenOverlay
          title="Confirm Save"
          width="20%"
          height="20%"
          isPopup={true}
          buttons={[
            { label: "No, Cancel", onClick: () => setShowConfirmModal(false) },
            {
              label: "Yes, Save",
              onClick: handleSaveConfirm,
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <FormTitle className="semi-bold">Want to save changes?</FormTitle>
          <ModalText className="body-text-regular regular neutral-800">
            Are you sure you want to save the changes you made to this Care
            Plan? These changes won’t affect the Care Plans that are already
            published.
          </ModalText>
        </FullScreenOverlay>
      )}

      {showDeleteConfirmModal && (
        <FullScreenOverlay
          title="Confirm Delete"
          width="20%"
          height="20%"
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => setShowDeleteConfirmModal(false),
            },
            {
              label: "Yes, Delete",
              onClick: handleRemoveItem,
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <FormTitle className="semi-bold">Confirm Deletion</FormTitle>
          <p>Are you sure you want to delete this item?</p>
        </FullScreenOverlay>
      )}

      {/* Add Item Overlay */}
      {showAddItemOverlay && (
        <AddItemOverlay
          allItems={allItems}
          selectedItemIds={selectedItemIds}
          onClose={() => setShowAddItemOverlay(false)}
          onUpdateSelectedItems={handleUpdateSelectedItems}
        />
      )}

      {/* Edit Contact FullScreenOverlay */}
      {showEditOverlay && editContact && (
        <FullScreenOverlay
          title="Edit Default Contact"
          onClose={() => {
            setShowEditOverlay(false);
            setEditContact(null);
          }}
          width="40%"
          height="50%"
          isPopup={false}
          iconClose={true}
          isSlimModal={true}
          footerButtons={editOverlayButtons}
        >
          <form id="contactEditForm" onSubmit={(e) => e.preventDefault()}>
            <label>Name</label>
            <TextField
              label="Name"
              name="defaultContactEditName"
              value={editForm.name}
              onChange={handleEditFormChange}
              placeholder="Enter name"
              required
            />
            <Spacer height="16px" />
            <label>Phone Number</label>
            <TextField
              label="Phone Number"
              name="defaultContactEditNumber"
              value={editForm.number}
              onChange={handleEditFormChange}
              placeholder="Enter phone number"
              required
            />
          </form>
        </FullScreenOverlay>
      )}

      {/* Confirmation overlay AFTER user clicks "Save" in edit overlay */}
      {showEditSaveConfirm && (
        <FullScreenOverlay
          title="Confirm Update"
          width="20%"
          height="20%"
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => setShowEditSaveConfirm(false),
            },
            {
              label: "Yes, Save",
              onClick: handleConfirmSaveContact,
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <FormTitle className="semi-bold">Confirm Contact Update</FormTitle>
          <p>Are you sure you want to save changes to this contact?</p>
        </FullScreenOverlay>
      )}

      {/* Confirm delete default contact */}
      {showDeleteContactOverlay && deleteContact && (
        <FullScreenOverlay
          title="Confirm Delete"
          width="20%"
          height="20%"
          isPopup={true}
          buttons={[
            {
              label: "No, Cancel",
              onClick: () => setShowDeleteContactOverlay(false),
            },
            {
              label: "Yes, Delete",
              onClick: handleConfirmDeleteContact,
              variant: "primary",
              state: "idle",
            },
          ]}
        >
          <FormTitle className="semi-bold">Delete Default Contact</FormTitle>
          <p>Are you sure you want to delete {deleteContact.name}?</p>
        </FullScreenOverlay>
      )}
    </>
  );
};

export default EditSection;

// Styled components (reuse from original component or define here)
const FormSection = styled.div`
  width: 60%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-100, #fff);
  box-shadow: 0px 10px 16px 0px rgba(230, 230, 230, 0.25);
`;

const FormTitle = styled.h5``;

const SectionCard = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: ${(props) => (props.mode === "edit" ? "0px" : "24px")};
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-300, #e9ecef);
  background: var(--Colors-Neutral-200, #f8f9fa);
  pointer-events: auto;

  &: active {
    opacity: 0.5;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2); // Clear visual cue that the item is being dragged
    // cursor: grabbing; // Change cursor to grabbing when the item is being dragged
  }
`;

const ItemTitle = styled.h6``;

const ItemTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const ItemTitleButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EditItemCard = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
  align-self: stretch;
`;

const ItemThumbnail = styled.div`
  width: 150px;
  height: 150px;
  background-color: #d9d9d9;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-400, #d9d9d9);
  overflow: hidden; /* Ensure that the image does not overflow the container */

  img {
    width: 100%;
    height: 100%;
    object-fit: cover; /* Ensures the image covers the container without distortion */
    border-radius: 12px; /* Make sure the image has rounded corners like the container */
  }
`;

const ItemDescription = styled.div`
  display: flex;
  flex: 1 0 0;
  padding: 12px 16px;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 12px;
  border: 1px solid var(--Colors-Neutral-400, #ced4da);
  min-height: 96px;
  background-color: ${(props) =>
    props.mode === "edit" ? "#fff" : "transparent"};

  /* Dynamic height handling  *******NOT GROWING TO 150 NEED TO CHECK******* */
  max-height: 150px; /* Set the maximum height for the description */
  overflow-y: auto; /* Add a scrollbar if the content exceeds the height */
  word-wrap: break-word; /* Ensure long words are wrapped */
  line-height: 1.5; /* Adjust line height to provide better readability */

  /* Allow the element to grow up to the max-height */
  height: auto; /* Let the height grow based on content */
  min-height: 96px; /* Set an initial minimum height */

  /* Optional: Scrollbar styling for better visibility */
  scrollbar-width: thin; /* Firefox */
  scrollbar-color: #ced4da transparent; /* Firefox */

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ced4da; /* Scrollbar color */
    border-radius: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent; /* Scrollbar track color */
  }
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  margin-top: 24px;
  align-self: stretch;
`;

const RadioButtonWrapper = styled.div`
  display: flex;
  flex: 1 0 0;
  height: 40px;
  align-items: center;
  padding: 24px;
  border-radius: 12px;
  border: 1px solid var(--Colors-Primary-500, #0065c0);
`;

const IconButtonWrapper = styled.div``;

const DragWrapper = styled.div`
  padding: 0;
  margin: 0;
  background-color: white;
  // display: contents;
  // margin-bottom: 24px;
  cursor: grab;
  &: active {
    cursor: grabbing; // Change cursor to grabbing when the item is being dragged
  }
`;

const ModalText = styled.p`
  text-align: center;
`;

const AddItemButton = styled.div`
  display: flex;
  padding: 50px 0px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  border-radius: 12px;
  border: 1px dashed var(--Colors-Primary-500, #0065c0);
`;
